
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useToast } from "@/components/ui/use-toast";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Link, useSearchParams } from "react-router-dom";
import { AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import WeeklyReportScheduler from "./WeeklyReportScheduler";
import SeoToolSelector from "./seo/SeoToolSelector";
import SeoSearchForm from "./seo/SeoSearchForm";
import SeoAnalysisProgress from "./seo/SeoAnalysisProgress";
import SeoScoreOverview from "./seo/SeoScoreOverview";
import SeoTabContent from "./seo/SeoTabContent";
import OpenAiInsights from "./seo/OpenAiInsights";
import { SeoResult } from "./seo/types";
import { validateUrl, getScoreColor } from "./seo/utils";
import { generateMockSeoResult } from "./seo/mockApiCalls";
import { recordUrlTest } from "@/utils/emailService";

export const SeoAnalysisTool = () => {
  const [searchParams] = useSearchParams();
  const [url, setUrl] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<SeoResult | null>(null);
  const [urlError, setUrlError] = useState<string | null>(null);
  const { toast } = useToast();
  const [selectedTool, setSelectedTool] = useState("seo-analysis");

  useEffect(() => {
    const toolParam = searchParams.get("tool");
    if (toolParam) {
      switch (toolParam) {
        case "keywords":
          setSelectedTool("keyword-research");
          break;
        case "backlinks":
          setSelectedTool("backlink-checker");
          break;
        case "speed":
          setSelectedTool("site-speed");
          break;
        default:
          setSelectedTool("seo-analysis");
      }
    }

    const urlParam = searchParams.get("url");
    if (urlParam) {
      setUrl(urlParam);
      analyzeWebsite(urlParam);
    }
  }, [searchParams]);

  const analyzeWebsite = async (websiteUrl: string) => {
    setAnalyzing(true);
    setProgress(0);
    setResult(null);

    // Record the URL test for lead tracking
    try {
      await recordUrlTest(websiteUrl);
    } catch (error) {
      console.error("Failed to record URL test:", error);
      // Continue with analysis even if recording fails
    }

    for (let i = 0; i <= 100; i += 5) {
      await new Promise((resolve) => setTimeout(resolve, 150));
      setProgress(i);
    }

    const mockResult = generateMockSeoResult(websiteUrl);
    setResult(mockResult);
    setAnalyzing(false);

    toast({
      title: "Analysis Complete",
      description: `Your website scored ${mockResult.score}/100 with ${mockResult.suggestions.length} improvement opportunities.`,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setUrlError(null);
    
    if (!url.trim()) {
      setUrlError("Please enter a URL.");
      return;
    }

    const [isValid, normalizedUrl] = validateUrl(url);
    if (!isValid) {
      setUrlError("Please enter a valid URL. Example: https://example.com");
      return;
    }

    setUrl(normalizedUrl);
    analyzeWebsite(normalizedUrl);
  };

  return (
    <section id="analysis-tool" className="section-padding bg-muted/50">
      <div className="page-container">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <span className="inline-block px-4 py-1.5 mb-6 text-sm font-medium rounded-full bg-desert-sunset/10 text-desert-sunset">
            Free SEO Tools
          </span>
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            See How Your Website <span className="text-gradient bg-sunset-gradient">Measures Up</span>
          </h2>
          <p className="text-lg text-foreground/70">
            Get an instant analysis of your website's performance with our suite of free SEO tools. No sign-up required.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto"
        >
          <div className="glassmorphism dark:glassmorphism-dark rounded-xl p-6 md:p-8">
            <Alert className="mb-6 bg-desert-sunset/10 border-desert-sunset/20">
              <AlertCircle className="h-4 w-4 text-desert-sunset" />
              <AlertDescription>
                <span className="font-medium">Demo Mode:</span> This tool currently uses simulated data for demonstration purposes. 
                For a full professional SEO audit with real data and AI-powered recommendations, please <Link to="/contact" className="underline font-medium">contact us</Link>.
              </AlertDescription>
            </Alert>
            
            <SeoToolSelector 
              selectedTool={selectedTool} 
              setSelectedTool={setSelectedTool} 
            />

            <SeoSearchForm
              url={url}
              setUrl={setUrl}
              analyzing={analyzing}
              urlError={urlError}
              handleSubmit={handleSubmit}
              selectedTool={selectedTool}
            />

            <SeoAnalysisProgress 
              analyzing={analyzing} 
              progress={progress} 
              selectedTool={selectedTool} 
            />

            {result && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6 }}
              >
                {/* Add OpenAI Insights component here */}
                <OpenAiInsights url={url} />
                
                <SeoScoreOverview result={result} getScoreColor={getScoreColor} />

                <Tabs defaultValue="overview" className="mt-8">
                  <TabsList className="grid grid-cols-2 md:grid-cols-5 mb-4">
                    <TabsTrigger value="overview">Overview</TabsTrigger>
                    <TabsTrigger value="keywords">Keywords</TabsTrigger>
                    <TabsTrigger value="pages">Pages</TabsTrigger>
                    <TabsTrigger value="competitors">Competitors</TabsTrigger>
                    <TabsTrigger value="recommendations">Recommendations</TabsTrigger>
                  </TabsList>
                  
                  {/* Tab Content */}
                  <SeoTabContent result={result} url={url} />
                </Tabs>
                
                <div className="mt-12 border-t pt-8">
                  <h3 className="text-xl font-bold mb-4">Get Weekly SEO Updates</h3>
                  <p className="text-muted-foreground mb-6">
                    Schedule free weekly reports about your website's SEO performance and get notified about changes in your rankings.
                  </p>
                  <WeeklyReportScheduler websiteUrl={url} />
                </div>
                
                <div className="mt-8 border-t pt-6 text-xs text-muted-foreground">
                  <p>
                    <strong>Data Source:</strong> {result.dataSource}
                  </p>
                </div>
              </motion.div>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default SeoAnalysisTool;
